import { WarningFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';
import cn from 'classnames';

import { Avatar } from '@app/components/Avatar';
import { TruncatedHeaderText } from '@app/shared/components';

import type { Employee } from '@pages/Calendar/data';
import { EmployeeHeaderActions } from '@pages/Calendar/ui/EmployeeHeaderActions';

import type { FC, WheelEventHandler } from 'react';

import '../styles/EmployeesHeadersStrip.scss';

interface EmployeesHeadersStripProps {
  employees: Employee[];
  columnWidth: number;
  totalTrackWidth: number;
  hasOverflow: boolean;
  trackTransform?: string;
  onWheel: WheelEventHandler<HTMLDivElement>;
}

export const EmployeesHeadersStrip: FC<EmployeesHeadersStripProps> = ({
  employees,
  columnWidth,
  totalTrackWidth,
  hasOverflow,
  trackTransform,
  onWheel,
}) => (
  <div
    className="EmployeesHeadersStrip"
    data-testid="employees-headers-strip"
    onWheel={onWheel}
  >
    <div
      className="EmployeesHeadersStrip__track"
      style={{
        width: hasOverflow && totalTrackWidth ? totalTrackWidth : '100%',
        transform: trackTransform,
      }}
    >
      {employees.map((employee) => {
        const {
          id,
          avatarUrl,
          firstName,
          hasActiveWorkday,
          isWorkdayDeleted,
          lastName,
          name,
          position,
        } = employee;
        const isArchived = isWorkdayDeleted === true;
        const hasSplitName = Boolean(lastName) || Boolean(firstName);
        const fallbackName =
          name || [lastName, firstName].filter(Boolean).join(' ');
        const hasPhoto = Boolean(avatarUrl);
        const headerAvatar = (
          <Avatar
            src={avatarUrl}
            nameFirst={firstName ?? name}
            nameLast={lastName}
            size={38}
            className="EmployeesHeadersStrip__avatar"
            data-testid={`employee-header-avatar-${id}`}
          />
        );

        return (
          <div
            key={id}
            className={cn('EmployeesHeadersStrip__cell', {
              'EmployeesHeadersStrip__cell--inactive-workday':
                hasActiveWorkday === false,
              'EmployeesHeadersStrip__cell--archived': isArchived,
            })}
            style={{
              width: hasOverflow && columnWidth ? columnWidth : undefined,
              flex:
                hasOverflow && columnWidth ? `0 0 ${columnWidth}px` : '1 1 0%',
            }}
            data-testid={`employee-header-${id}`}
          >
            <div className="EmployeesHeadersStrip__avatar-and-name">
              {hasPhoto ? (
                <Tooltip
                  placement="top"
                  title={
                    <div className="EmployeesHeadersStrip__avatar-tooltip">
                      <Avatar
                        src={avatarUrl}
                        nameFirst={firstName ?? name}
                        nameLast={lastName}
                        size={76}
                        data-testid={`employee-header-avatar-enlarged-${id}`}
                      />
                    </div>
                  }
                  styles={{ body: { padding: '4px', borderRadius: '50%' } }}
                >
                  <span className="EmployeesHeadersStrip__avatar-trigger">
                    {headerAvatar}
                  </span>
                </Tooltip>
              ) : (
                headerAvatar
              )}
              <div className="EmployeesHeadersStrip__main-info">
                {isArchived && (
                  <span className="EmployeesHeadersStrip__archive-label">
                    <WarningFilled /> В архиве
                  </span>
                )}
                {hasSplitName ? (
                  <>
                    {lastName && (
                      <TruncatedHeaderText
                        className="EmployeesHeadersStrip__name"
                        lineCount={1}
                        text={lastName}
                        testId={`employee-header-last-name-${id}`}
                      />
                    )}
                    {firstName && (
                      <TruncatedHeaderText
                        className="EmployeesHeadersStrip__name"
                        lineCount={1}
                        text={firstName}
                        testId={`employee-header-first-name-${id}`}
                      />
                    )}
                  </>
                ) : (
                  <TruncatedHeaderText
                    className="EmployeesHeadersStrip__name"
                    lineCount={2}
                    text={fallbackName}
                    testId={`employee-header-name-${id}`}
                  />
                )}
                {position && (
                  <TruncatedHeaderText
                    className="EmployeesHeadersStrip__position"
                    lineCount={2}
                    text={position}
                    testId={`employee-header-position-${id}`}
                  />
                )}
              </div>
            </div>
            <div className="EmployeesHeadersStrip__actions">
              <EmployeeHeaderActions employee={employee} />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);
